import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, timer } from 'rxjs';
import {
  TokenAnalyticsService,
  TokenStatsAgentRow,
  TokenStatsResponse,
  TokenStatsSnapshotMeta,
} from '../../services/token-analytics.service';

/**
 * Super-admin token analytics (LangSmith agent token economy, migration 108).
 *
 * Renders the persisted snapshot for a selectable window; a refresh runs
 * asynchronously in al-tutor (minutes — LangSmith rate limit), so after
 * triggering one we poll getStats until the run leaves `running`.
 */

interface HistogramSegment {
  cls: string;
  pct: number;
  count: number;
  label: string;
}

interface DistributionRow {
  row: TokenStatsAgentRow;
  left: number;
  width: number;
  meanPos: number;
  tailPos: number;
}

const WINDOW_OPTIONS = [30, 90, 180, 399];
const POLL_MS = 10_000;
// Log scale bounds, same as the original one-off dashboard: 300 .. 1M tokens.
const LOG_LO = Math.log10(300);
const LOG_HI = Math.log10(1_000_000);

@Component({
  selector: 'app-token-analytics',
  templateUrl: './token-analytics.component.html',
  styleUrl: './token-analytics.component.scss',
})
export class TokenAnalyticsComponent implements OnInit, OnDestroy {
  readonly windowOptions = WINDOW_OPTIONS;
  windowDays = 399;

  isLoading = false;
  isRefreshing = false;
  snapshot: TokenStatsSnapshotMeta | null = null;
  latestRun: TokenStatsSnapshotMeta | null = null;

  allRows: TokenStatsAgentRow[] = [];
  projects: string[] = [];
  selectedProject = '';

  private pollSub?: Subscription;

  /** Aktiver Reiter, in `?tab=` gespiegelt wie auf „Organisation". */
  readonly tabIds = ['tokens', 'margin', 'routing'] as const;
  activeTab: string = 'tokens';

  constructor(
    private api: TokenAnalyticsService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const requested = this.route.snapshot.queryParamMap.get('tab');
    if (requested && this.tabIds.includes(requested as any)) {
      this.activeTab = requested;
    }
    this.load();
  }

  onTabChange(tabId: string): void {
    if (!this.tabIds.includes(tabId as any) || tabId === this.activeTab) return;
    this.activeTab = tabId;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tabId },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  onWindowChange(days: number): void {
    this.windowDays = days;
    this.load();
  }

  onProjectChange(project: string): void {
    this.selectedProject = project;
  }

  load(): void {
    this.isLoading = true;
    this.api.getStats(this.windowDays).subscribe({
      next: (res) => this.applyResponse(res),
      error: () => (this.isLoading = false),
    });
  }

  refresh(): void {
    if (this.isRefreshing) {
      return;
    }
    this.isRefreshing = true;
    this.api.refresh(this.windowDays).subscribe({
      next: () => this.startPolling(),
      error: (err) => {
        if (err?.status === 409) {
          // A run is already in flight (other admin or cron) — just follow it.
          this.startPolling();
        } else {
          this.isRefreshing = false;
        }
      },
    });
  }

  private applyResponse(res: TokenStatsResponse): void {
    this.isLoading = false;
    this.snapshot = res.snapshot;
    this.latestRun = res.latest_run;
    this.allRows = res.rows || [];

    this.projects = [...new Set(this.allRows.map((r) => r.project_name))];
    if (!this.projects.includes(this.selectedProject)) {
      // Default to the busiest project.
      const byRuns = new Map<string, number>();
      for (const r of this.allRows) {
        byRuns.set(r.project_name, (byRuns.get(r.project_name) || 0) + r.runs);
      }
      this.selectedProject =
        [...byRuns.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || '';
    }

    if (res.refresh_running || res.latest_run?.status === 'running') {
      this.startPolling();
    } else {
      this.isRefreshing = false;
      this.stopPolling();
    }
  }

  private startPolling(): void {
    this.isRefreshing = true;
    if (this.pollSub) {
      return;
    }
    this.pollSub = timer(POLL_MS, POLL_MS).subscribe(() => {
      this.api.getStats(this.windowDays).subscribe({
        next: (res) => this.applyResponse(res),
      });
    });
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }

  // ── Derived view data ────────────────────────────────────────────────

  get rows(): TokenStatsAgentRow[] {
    return this.allRows.filter((r) => r.project_name === this.selectedProject);
  }

  /** Busiest agent of the selected project — the KPI reference. */
  get primaryAgent(): TokenStatsAgentRow | null {
    return this.rows.reduce<TokenStatsAgentRow | null>(
      (best, r) => (!best || r.runs > best.runs ? r : best),
      null,
    );
  }

  get totalCost(): number {
    return this.rows.reduce((sum, r) => sum + (r.total_cost || 0), 0);
  }

  get lastFetchedAt(): string | null {
    return this.snapshot?.finished_at || null;
  }

  get refreshFailed(): boolean {
    return !this.isRefreshing && this.latestRun?.status === 'failed';
  }

  private logPos(v: number | null | undefined): number {
    const value = Math.max(v || 300, 300);
    return ((Math.log10(value) - LOG_LO) / (LOG_HI - LOG_LO)) * 100;
  }

  get axisTicks(): { label: string; pos: number }[] {
    return [1_000, 10_000, 100_000, 1_000_000].map((t) => ({
      label: t >= 1_000_000 ? '1M' : `${t / 1000}k`,
      pos: this.logPos(t),
    }));
  }

  get distributionRows(): DistributionRow[] {
    return this.rows.map((row) => {
      const left = this.logPos(row.p50_total);
      const right = this.logPos(row.p99_total);
      return {
        row,
        left,
        width: Math.max(right - left, 0.6),
        meanPos: this.logPos(row.mean_total),
        tailPos: this.logPos(700_000),
      };
    });
  }

  histogram(row: TokenStatsAgentRow): HistogramSegment[] {
    const buckets = [
      { cls: 'b0', count: row.runs - row.gt_20k, label: '<20k' },
      { cls: 'b1', count: row.gt_20k - row.gt_40k, label: '20–40k' },
      { cls: 'b2', count: row.gt_40k - row.gt_100k, label: '40–100k' },
      { cls: 'b3', count: row.gt_100k - row.gt_500k, label: '100–500k' },
      { cls: 'b4', count: row.gt_500k, label: '>500k' },
    ];
    return buckets
      .filter((b) => b.count > 0)
      .map((b) => ({
        cls: b.cls,
        pct: (b.count / row.runs) * 100,
        count: b.count,
        label: b.label,
      }));
  }
}
